import { createContext, useContext, useMemo } from 'react'
import { Outlet } from 'react-router'
import { useSession } from '@/session/SessionContext'
import { Subscription } from '@/types/subscription'
import { WorkspaceMember, WorkspaceMemberStatus } from '@/types/workspace'
import { CreateWorkspaceHandler } from './CreateWorkspaceHandler'
import { NoActiveWorkspaceAlert } from './WorkspaceAlerts/NoActiveWorkspaceAlert'
import { NoSubscriptionAlert } from './WorkspaceAlerts/NoSubscriptionAlert'

type ContextProps = {
  membership: WorkspaceMember | null
  subscription: Subscription | null
}

const initialValues = {
  membership: null,
  subscription: null
}

const WorkspaceContext = createContext<ContextProps>(initialValues)

export const WorkspaceProvider = () => {
  const {
    refreshSession,
    subscriptions,
    workspaceMemberships,
    activeMembership
  } = useSession()

  const subscription = useMemo(() => subscriptions[0] || null, [subscriptions])

  const activeWorkspaces = useMemo(
    () =>
      workspaceMemberships.filter(
        (membership) => membership.status === WorkspaceMemberStatus.Active
      ),
    [workspaceMemberships]
  )

  const contextValue = useMemo(
    () => ({
      membership: activeMembership,
      subscription
    }),
    [activeMembership, subscription]
  )

  const handleWorkspaceCreated = () => {
    void refreshSession() // Refresh session to get updated workspace permissions
  }

  // If user has no subscription, show no subscription alert
  if (!subscription) {
    return (
      <NoSubscriptionAlert
        activeMembership={activeMembership || undefined}
        activeWorkspaces={activeWorkspaces}
      />
    )
  }

  // If user has no workspace memberships, show create workspace page
  if (workspaceMemberships.length === 0) {
    return (
      <CreateWorkspaceHandler onWorkspaceCreated={handleWorkspaceCreated} />
    )
  }

  // If user has no active membership, show no membership alert
  if (!activeMembership) {
    return <NoActiveWorkspaceAlert />
  }

  return (
    <WorkspaceContext.Provider value={contextValue}>
      <Outlet />
    </WorkspaceContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useWorkspace = () => {
  const { membership, subscription } = useContext(WorkspaceContext)

  if (!membership || !subscription) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider')
  }

  const { workspace, ...membershipWithoutWorkspace } = membership

  return { membership: membershipWithoutWorkspace, workspace, subscription }
}
